import { useState } from 'react'
import { Search, Loader2, FileText } from 'lucide-react'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import {
  useAdminJurisdictions,
  useAdminPubAuthorities,
  useAdminDocuments,
  useDocumentViewCoverage,
} from '@/hooks/useAdminDatabase'
import type { AdminJurisdiction, AdminPubAuthority, CoverageGroup } from '@/api/adminService'
import { useGenerateSingleView } from '@/hooks/useIngestion'
import LoadingSpinner from '@/components/admin/shared/LoadingSpinner'
import EmptyState from '@/components/admin/shared/EmptyState'
import Pagination from '@/components/admin/shared/Pagination'

type Section = 'jurisdictions' | 'pub-authorities' | 'documents' | 'view-coverage'

const PAGE_SIZE = 50

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildJurisdictionMap(jurisdictions: AdminJurisdiction[]): Map<string, string> {
  return new Map(jurisdictions.map((j) => [j.jurisdiction_uuid, j.jurisdiction_common_name]))
}

function buildPubAuthorityMap(pubAuthorities: AdminPubAuthority[]): Map<string, string> {
  return new Map(pubAuthorities.map((pa) => [pa.uuid, pa.name]))
}

function UUIDChips({ uuids, labelMap, color }: {
  uuids: string[]
  labelMap: Map<string, string>
  color: 'blue' | 'purple'
}) {
  if (!uuids.length) return <span className="text-xs text-gray-300">—</span>

  const colorClass = color === 'blue'
    ? 'bg-blue-50 text-blue-700 border-blue-200'
    : 'bg-purple-50 text-purple-700 border-purple-200'

  return (
    <div className="flex flex-wrap gap-1">
      {uuids.map((uuid) => (
        <span key={uuid} className={`inline-block px-1.5 py-0.5 text-xs rounded border ${colorClass}`}>
          {labelMap.get(uuid) ?? uuid.slice(0, 8) + '…'}
        </span>
      ))}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DatabasePage() {
  const [section, setSection] = useState<Section>('jurisdictions')

  const sections: { id: Section; label: string }[] = [
    { id: 'jurisdictions',   label: 'Jurisdictions'          },
    { id: 'pub-authorities', label: 'Publishing Authorities' },
    { id: 'documents',       label: 'Documents'              },
    { id: 'view-coverage',   label: 'View Coverage'          },
  ]

  return (
    <div>
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-6">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={clsx(
              'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
              section === s.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'jurisdictions'   && <JurisdictionsSection />}
      {section === 'pub-authorities' && <PubAuthoritiesSection />}
      {section === 'documents'       && <DocumentsSection />}
      {section === 'view-coverage'   && <ViewCoverageSection />}
    </div>
  )
}

// ─── Jurisdictions ────────────────────────────────────────────────────────────

function JurisdictionsSection() {
  const { data, isLoading, error } = useAdminJurisdictions()

  if (isLoading) return <LoadingSpinner />
  if (error)     return <p className="text-red-500 text-sm py-8 text-center">Failed to load jurisdictions.</p>
  if (!data?.length) return <EmptyState message="No jurisdictions found" />

  return (
    <div>
      <p className="text-xs text-gray-400 mb-4">{data.length} jurisdictions</p>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 py-3">Common Name</th>
              <th className="px-4 py-3">Full Name</th>
              <th className="px-4 py-3">UUID</th>
              <th className="px-4 py-3">Last Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((j) => (
              <tr key={j.jurisdiction_uuid} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{j.jurisdiction_common_name}</td>
                <td className="px-4 py-3 text-gray-600">{j.jurisdiction_name}</td>
                <td className="px-4 py-3 text-gray-400 text-xs font-mono">{j.jurisdiction_uuid}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">
                  {j.last_updated ? format(new Date(j.last_updated), 'MMM d, yyyy') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Publishing Authorities ───────────────────────────────────────────────────

function PubAuthoritiesSection() {
  const { data, isLoading, error } = useAdminPubAuthorities()
  const { data: jurisdictions } = useAdminJurisdictions()

  const jurisdictionMap = jurisdictions ? buildJurisdictionMap(jurisdictions) : new Map<string, string>()

  if (isLoading) return <LoadingSpinner />
  if (error)     return <p className="text-red-500 text-sm py-8 text-center">Failed to load publishing authorities.</p>
  if (!data?.length) return <EmptyState message="No publishing authorities found" />

  return (
    <div>
      <p className="text-xs text-gray-400 mb-4">{data.length} publishing authorities</p>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Jurisdictions</th>
              <th className="px-4 py-3">UUID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((pa) => (
              <tr key={pa.uuid} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{pa.name}</td>
                <td className="px-4 py-3 text-gray-500 font-mono text-xs">{pa.code}</td>
                <td className="px-4 py-3">
                  <UUIDChips uuids={pa.jurisdiction_uuids} labelMap={jurisdictionMap} color="blue" />
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs font-mono">{pa.uuid}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── View Coverage ────────────────────────────────────────────────────────────

function CoverageBar({ view, pdf, missing }: { view: number; pdf: number; missing: number }) {
  const total = view + pdf + missing
  if (total === 0) return null
  return (
    <div className="flex rounded overflow-hidden h-2 w-full min-w-16">
      {view    > 0 && <div className="bg-green-400"  style={{ width: `${(view    / total) * 100}%` }} title={`View: ${view}`} />}
      {pdf     > 0 && <div className="bg-blue-400"   style={{ width: `${(pdf     / total) * 100}%` }} title={`PDF: ${pdf}`} />}
      {missing > 0 && <div className="bg-red-300"    style={{ width: `${(missing / total) * 100}%` }} title={`Missing: ${missing}`} />}
    </div>
  )
}

const COVERAGE_PAGE_SIZE = 50

function GroupTable({ groups, title }: { groups: CoverageGroup[]; title: string }) {
  const [showAll, setShowAll] = useState(false)
  if (!groups.length) return null

  const withMissing = groups.filter(g => g.missing > 0)
  const displayed = showAll ? groups : withMissing

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        {withMissing.length < groups.length && (
          <button
            onClick={() => setShowAll(v => !v)}
            className="text-xs text-blue-600 hover:underline"
          >
            {showAll ? `Show only with missing (${withMissing.length})` : `Show all (${groups.length})`}
          </button>
        )}
      </div>
      {displayed.length === 0 ? (
        <p className="text-xs text-gray-400 mb-6">No entries with missing documents.</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 text-right">View</th>
                <th className="px-4 py-3 text-right">PDF</th>
                <th className="px-4 py-3 text-right text-red-500">Missing</th>
                <th className="px-4 py-3 w-40">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayed.map((g) => (
                <tr key={g.uuid} className={clsx('hover:bg-gray-50', g.missing > 0 && 'bg-red-50/30')}>
                  <td className="px-4 py-2 text-gray-800">{g.name}</td>
                  <td className="px-4 py-2 text-right text-green-700 font-mono text-xs">{g.view}</td>
                  <td className="px-4 py-2 text-right text-blue-700 font-mono text-xs">{g.pdf}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {g.missing > 0
                      ? <span className="text-red-600 font-semibold">{g.missing}</span>
                      : <span className="text-gray-300">0</span>}
                  </td>
                  <td className="px-4 py-2">
                    <CoverageBar view={g.view} pdf={g.pdf} missing={g.missing} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ViewCoverageSection() {
  const { data, isLoading, error, refetch } = useDocumentViewCoverage()
  const { data: jurisdictions } = useAdminJurisdictions()
  const { data: pubAuthorities } = useAdminPubAuthorities()
  const [missingPage, setMissingPage] = useState(1)
  const { mutate: generateView, isPending: isGenerating } = useGenerateSingleView()
  const [generatingUuid, setGeneratingUuid] = useState<string | null>(null)
  const jurisdictionMap = jurisdictions ? buildJurisdictionMap(jurisdictions) : new Map<string, string>()
  const pubAuthorityMap = pubAuthorities ? buildPubAuthorityMap(pubAuthorities) : new Map<string, string>()

  if (isLoading) return <LoadingSpinner />
  if (error) return <p className="text-red-500 text-sm py-8 text-center">Failed to load coverage data.</p>
  if (!data) return null

  const { summary, missing_docs, by_jurisdiction, by_pub_authority } = data
  const missingTotalPages = Math.ceil(missing_docs.length / COVERAGE_PAGE_SIZE)
  const pagedMissing = missing_docs.slice((missingPage - 1) * COVERAGE_PAGE_SIZE, missingPage * COVERAGE_PAGE_SIZE)

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        {([
          { label: 'Total Docs',    value: summary.total,   color: 'text-gray-800' },
          { label: 'View JSON',     value: summary.view,    color: 'text-green-600' },
          { label: 'PDF',           value: summary.pdf,     color: 'text-blue-600' },
          { label: 'Missing',       value: summary.missing, color: 'text-red-600' },
        ] as const).map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-400 inline-block" /> View JSON — regulation with a generated JSON view</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" /> PDF — standards document served as PDF</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-300 inline-block" /> Missing — regulation with no view JSON yet</span>
      </div>

      {/* By jurisdiction / by pub authority */}
      <GroupTable groups={by_jurisdiction} title="By Jurisdiction" />
      <GroupTable groups={by_pub_authority} title="By Publishing Authority" />

      {/* Missing docs table */}
      {missing_docs.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Missing Documents
            <span className="ml-2 text-xs font-normal text-gray-400">({missing_docs.length} regulation{missing_docs.length !== 1 ? 's' : ''} without a view JSON)</span>
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Legal Ref</th>
                  <th className="px-4 py-3">Lang</th>
                  <th className="px-4 py-3">Jurisdictions</th>
                  <th className="px-4 py-3">Publishing Authorities</th>
                  <th className="px-4 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedMissing.map((doc) => (
                  <tr key={doc.legislation_uuid} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-gray-800 max-w-xs">{doc.title}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs whitespace-nowrap">{doc.legal_ref}</td>
                    <td className="px-4 py-3 text-gray-500 uppercase text-xs">{doc.language_code}</td>
                    <td className="px-4 py-3">
                      <UUIDChips uuids={doc.jurisdiction_uuids} labelMap={jurisdictionMap} color="blue" />
                    </td>
                    <td className="px-4 py-3">
                      <UUIDChips uuids={doc.publishing_authority_uuids} labelMap={pubAuthorityMap} color="purple" />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setGeneratingUuid(doc.legislation_uuid)
                          generateView(doc.legislation_uuid, {
                            onSuccess: () => { setGeneratingUuid(null); refetch() },
                            onError: () => setGeneratingUuid(null),
                          })
                        }}
                        disabled={isGenerating}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-brand text-white rounded hover:bg-brand/90 disabled:opacity-50"
                      >
                        {isGenerating && generatingUuid === doc.legislation_uuid
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <FileText className="w-3 h-3" />}
                        Generate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={missingPage} totalPages={missingTotalPages} onPageChange={setMissingPage} />
        </div>
      )}

      {missing_docs.length === 0 && (
        <EmptyState message="All regulation documents have view JSONs" />
      )}
    </div>
  )
}

// ─── Documents ────────────────────────────────────────────────────────────────

function DocumentsSection() {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [jurisdictionUuid, setJurisdictionUuid] = useState('')
  const [pubAuthorityUuid, setPubAuthorityUuid] = useState('')

  const { data: jurisdictions } = useAdminJurisdictions()
  const { data: pubAuthorities } = useAdminPubAuthorities()
  const { data, isLoading, error } = useAdminDocuments(
    page, PAGE_SIZE, search, jurisdictionUuid || undefined, pubAuthorityUuid || undefined,
  )

  const jurisdictionMap = jurisdictions ? buildJurisdictionMap(jurisdictions) : new Map<string, string>()
  const pubAuthorityMap = pubAuthorities ? buildPubAuthorityMap(pubAuthorities) : new Map<string, string>()

  // Filter pub authority dropdown to those belonging to the selected jurisdiction
  const filteredPubAuthorities = jurisdictionUuid && pubAuthorities
    ? pubAuthorities.filter((pa) => pa.jurisdiction_uuids.includes(jurisdictionUuid))
    : pubAuthorities

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  function handleJurisdictionChange(uuid: string) {
    setJurisdictionUuid(uuid)
    setPubAuthorityUuid('') // reset pub authority when jurisdiction changes
    setPage(1)
  }

  function handlePubAuthorityChange(uuid: string) {
    setPubAuthorityUuid(uuid)
    setPage(1)
  }

  function clearFilters() {
    setSearch('')
    setSearchInput('')
    setJurisdictionUuid('')
    setPubAuthorityUuid('')
    setPage(1)
  }

  const hasFilters = search || jurisdictionUuid || pubAuthorityUuid

  return (
    <div>
      {/* Search + filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title or legal ref..."
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>
          <button
            type="submit"
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Search
          </button>
        </form>

        <select
          value={jurisdictionUuid}
          onChange={(e) => handleJurisdictionChange(e.target.value)}
          className="pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
        >
          <option value="">All jurisdictions</option>
          {jurisdictions?.map((j) => (
            <option key={j.jurisdiction_uuid} value={j.jurisdiction_uuid}>
              {j.jurisdiction_common_name}
            </option>
          ))}
        </select>

        <select
          value={pubAuthorityUuid}
          onChange={(e) => handlePubAuthorityChange(e.target.value)}
          className="pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
        >
          <option value="">All pub authorities</option>
          {filteredPubAuthorities?.map((pa) => (
            <option key={pa.uuid} value={pa.uuid}>{pa.name}</option>
          ))}
        </select>

        {hasFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-1.5 text-sm text-gray-500 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Clear
          </button>
        )}

        {data && (
          <span className="ml-auto self-center text-xs text-gray-400">{data.total} documents</span>
        )}
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <p className="text-red-500 text-sm py-8 text-center">Failed to load documents.</p>
      ) : !data?.items.length ? (
        <EmptyState message="No documents found" />
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Legal Ref</th>
                  <th className="px-4 py-3">Lang</th>
                  <th className="px-4 py-3">Jurisdictions</th>
                  <th className="px-4 py-3">Publishing Authorities</th>
                  <th className="px-4 py-3">Last Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.items.map((doc) => (
                  <tr key={doc.legislation_uuid} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-gray-800 max-w-xs">
                      {doc.url_link ? (
                        <a
                          href={doc.url_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline text-blue-600"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {doc.title}
                        </a>
                      ) : (
                        doc.title
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs whitespace-nowrap">{doc.legal_ref}</td>
                    <td className="px-4 py-3 text-gray-500 uppercase text-xs">{doc.language_code}</td>
                    <td className="px-4 py-3">
                      <UUIDChips uuids={doc.jurisdiction_uuids} labelMap={jurisdictionMap} color="blue" />
                    </td>
                    <td className="px-4 py-3">
                      <UUIDChips uuids={doc.publishing_authority_uuids} labelMap={pubAuthorityMap} color="purple" />
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                      {doc.last_updated ? format(new Date(doc.last_updated), 'MMM d, yyyy') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={data.total_pages} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
